import React, { useEffect, useState } from "react";
import { Button, Col, Row } from "reactstrap";
import { useTranslation } from "react-i18next";
import { GetOrderStatsAPI } from "network/orders";
import { BarCard, Delta, Kpi, PeriodSelect, TableCard, dayLabel, downloadCsv, eur } from "components/stats/StatBits";

const OrderStats = () => {
  const { t } = useTranslation("orders");
  const [days, setDays] = useState(7);
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    setErr("");
    GetOrderStatsAPI(days).then(setD).catch((e) => setErr(e.message || t("stats.loadError")));
  }, [days, t]);

  if (err) return <p className="text-danger">{err}</p>;
  if (!d) return <p>{t("stats.loading")}</p>;
  const s = d.summary;
  const o = d.outcomes;
  const many = d.by_day.length > 31;

  const exportCsv = () =>
    downloadCsv(`${t("stats.csvName")}_${d.from}_${d.to}.csv`, [t("stats.csvDate"), t("stats.csvOrders"), t("stats.csvSales")], d.by_day.map((r) => [r.date, r.orders, Number(r.sales).toFixed(2).replace(".", ",")]));

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <PeriodSelect days={days} onChange={setDays} />
        <Button size="sm" color="primary" onClick={exportCsv}>{t("stats.export")}</Button>
      </div>
      <Row>
        <Kpi label={t("stats.orders")} value={s.orders} sub={<Delta now={s.orders} prev={s.prev_orders} />} />
        <Kpi label={t("stats.sales")} value={eur(s.sales)} sub={<Delta now={s.sales} prev={s.prev_sales} />} />
        <Kpi label={t("stats.average")} value={eur(s.average)} />
        <Kpi label={t("stats.clients")} value={d.clients.total} sub={<span className="small text-muted">{t("stats.clientsSub", { new: d.clients.new, returning: d.clients.returning })}</span>} />
      </Row>
      <Row>
        <Col lg="8">
          <BarCard title={t("stats.salesPerDay")} labels={d.by_day.map((r) => dayLabel(r.date))} data={d.by_day.map((r) => r.sales)} label={t("stats.sales")} money height={many ? 260 : 220} />
        </Col>
        <Col lg="4">
          <TableCard
            title={t("stats.outcomeTitle")}
            cols={[["k", t("stats.outcome")], ["n", t("stats.orders"), true]]}
            rows={o.received ? [
              { k: t("stats.accepted"), n: o.accepted }, ...(o.waiting ? [{ k: t("stats.waiting"), n: o.waiting }] : []), { k: t("stats.rejected"), n: o.rejected },
              { k: t("stats.missed"), n: o.missed }, { k: t("stats.cancelled"), n: o.cancelled }, { k: t("stats.totalReceived"), n: o.received },
            ] : []}
          />
        </Col>
      </Row>
      <Row>
        <Col lg="6">
          <TableCard title={t("stats.topItems")} cols={[["name", t("stats.dish")], ["quantity", t("stats.pieces"), true], ["sales", t("stats.sales"), true, eur]]} rows={d.top_items} />
        </Col>
        <Col lg="6">
          <TableCard title={t("stats.categories")} cols={[["name", t("stats.category")], ["quantity", t("stats.pieces"), true], ["sales", t("stats.sales"), true, eur]]} rows={d.categories} />
          <BarCard title={t("stats.byHour")} labels={d.by_hour.map((r) => `${r.hour}:00`)} data={d.by_hour.map((r) => r.orders)} label={t("stats.orders")} height={180} />
        </Col>
      </Row>
      <Row>
        <Col lg="4">
          <TableCard title={t("stats.paymentMethod")} cols={[["method", t("stats.method"), false, (v) => t(`stats.pay.${v}`, { defaultValue: v })], ["orders", t("stats.orders"), true], ["sales", t("stats.sales"), true, eur]]} rows={d.by_payment} />
        </Col>
        <Col lg="4">
          <TableCard title={t("stats.orderType")} cols={[["kind", t("stats.type"), false, (v) => t(`stats.kind.${v}`, { defaultValue: v })], ["orders", t("stats.orders"), true], ["sales", t("stats.sales"), true, eur]]} rows={d.by_kind} />
        </Col>
        <Col lg="4">
          <TableCard
            title={t("stats.promosUsed")}
            cols={[["name", t("stats.promotion")], ["orders", t("stats.orders"), true], ["discount", t("stats.discountGiven"), true, eur]]}
            rows={d.promotions}
            empty={t("stats.noPromos")}
          />
          <p className="small text-muted">{t("stats.totals", { discounts: eur(s.discounts), fees: eur(s.delivery_fees) })}</p>
        </Col>
      </Row>
    </>
  );
};

export default OrderStats;
